import { useCallback, useId, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn, PRODUCT_LOGOS, Reveal, useDismiss, useInView } from "./shared";

const RENTSHIELD_SERVICES = [
  {
    id: "on-rent",
    title: "On-Rent Commercial Auto Insurance",
    heading: ["On-Rent Commercial", "Auto Insurance"],
    copy: "Access primary commercial auto insurance for eligible rental periods, built for short-term, long-term, and car-sharing operations.",
    panelTitle: "Commercial auto policy",
    panelSubtitle: "Primary, A-rated coverage",
    status: "Active on rent",
    listLabel: "Designed for",
    items: ["Liability & Full coverage plans", "Short & Long-term programs", "Leisure Rentals", "Gig and rideshare use cases", "Direct & Private rental programs"],
  },
  {
    id: "roadside",
    title: "Roadside Assistance Program",
    heading: ["Roadside Assistance", "Program"],
    copy: "Give renters 24/7 roadside support with one structured program for towing, lockouts, and fuel events across your fleet.",
    panelTitle: "Roadside program",
    panelSubtitle: "Available on every trip",
    status: "24/7 support",
    listLabel: "Includes",
    items: ["Towing and recovery", "Jump starts", "Flat tire service", "Lockout assistance", "Fuel delivery"],
  },
]

const RENTID_SERVICES = [
  {
    id: "id-biometrics",
    title: "ID & Biometrics",
    copy: "Verify driver identity using government-issued identification and biometric verification. Take a selfie, a picture of the driver's license, press submit.",
    panelTitle: "Identity verification",
    panelSubtitle: "Government ID + selfie match",
    status: "Verified",
    listLabel: "Checks",
    items: ["Government-issued ID scan", "Biometric selfie match", "Liveness detection", "Document authenticity review"],
  },
  {
    id: "license-status",
    title: "Driver's License Status Check",
    heading: ["Driver's License", "Status Check"],
    copy: "Confirm license validity before every rental and surface suspensions, expirations, and license-class details in one check.",
    panelTitle: "License status",
    panelSubtitle: "Checked before every rental",
    status: "Valid",
    listLabel: "Confirms",
    items: ["License validity", "Suspensions and revocations", "Expiration date", "License class"],
  },
  {
    id: "criminal",
    title: "Criminal Background Check",
    heading: ["Criminal Background", "Check"],
    copy: "Add criminal background screening to eligible driver-verification workflows, with results and audit-ready reporting in your dashboard.",
    panelTitle: "Background screening",
    panelSubtitle: "Enterprise driver workflows",
    status: "Cleared",
    listLabel: "Includes",
    items: ["Criminal record screening", "Eligibility-based workflows", "Results in your dashboard", "Audit-ready reporting"],
  },
  {
    id: "mvr",
    title: "Motor Vehicle Record Check",
    heading: ["Motor Vehicle", "Record Check"],
    copy: "Access available driving-history records to surface violations, at-fault accidents, and risk signals for driver eligibility decisions.",
    panelTitle: "Driving history",
    panelSubtitle: "Motor vehicle record",
    status: "Reviewed",
    listLabel: "Surfaces",
    items: ["Moving violations", "At-fault accidents", "Major offenses", "Eligibility and risk signals"],
  },
  {
    id: "insurance-validation",
    title: "Auto Insurance Validation Check",
    heading: ["Auto Insurance", "Validation Check"],
    copy: "Validate renter-provided personal auto insurance, confirming active policy status, named insured, and coverage limits before pickup.",
    panelTitle: "Insurance validation",
    panelSubtitle: "Bring-your-own coverage",
    status: "Validated",
    listLabel: "Validates",
    items: ["Active policy status", "Named insured match", "Coverage limits", "Policy effective dates"],
  },
]

const RENTSUCCESS_SERVICE = {
  id: "tolls",
  title: "Tolls & Tickets Automation",
  copy: "Automate the identification, attribution, and management of tolls and traffic violations associated with rental activity, so every charge is captured and recovered.",
  panelTitle: "Tolls management",
  panelSubtitle: "Tolls, tracked and billed",
  listLabel: "Automated",
  items: ["Automatic toll capture per trip", "Transponder & plate tracking", "Reconciled in one dashboard"],
}

const RENTDIRECT_SERVICE = {
  id: "platform",
  title: "Platform & Marketplace Infrastructure",
  copy: "Power your own rental platform or marketplace with rental infrastructure for search, bookings, and checkout.",
  panelTitle: "Partner program",
  panelSubtitle: "Platforms and marketplaces",
  status: "Now accepting",
  listLabel: "Includes",
  items: ["White-label booking flows", "Operator onboarding", "Payments and payouts", "Embedded insurance and verification"],
}


/* Card glows (v0 mixes the brand colours in oklch; enterprise.css carries the rgba equivalents). */
const ACCENTS = {
  coral: { dot: "bg-brand-coral", check: "bg-brand-coral text-primary-foreground", glow: "ent-glow-coral" },
  violet: { dot: "bg-brand-violet", check: "bg-brand-violet text-primary-foreground", glow: "ent-glow-violet" },
  blue: { dot: "bg-brand-blue", check: "bg-brand-blue text-primary-foreground", glow: "ent-glow-blue" },
  mint: { dot: "bg-brand-mint", check: "bg-brand-mint text-foreground", glow: "ent-glow-mint" },
};

/* RentShield + INSHUR lockup, as on the home page's RentShield card (light-background copy of
   /logos/products/rentshield-inshur-white.svg). */
const RENTSHIELD_INSHUR_LOGO = { src: "/logos/products/rentshield-inshur.svg", width: 193, height: 65, alt: "RentShield powered by INSHUR" };

function ProductLogo({ product, logo = PRODUCT_LOGOS[product] }) {
  return (
    <img
      src={logo.src}
      alt={logo.alt || product}
      width={logo.width}
      height={logo.height}
      className="-ml-1 h-12 w-auto self-start object-contain"
    />
  );
}

/* Single-select dropdown (stands in for v0's Radix Select). */
function ServiceSelect({ label, services, value, onChange }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const rootRef = useDismiss(open, close);
  const buttonRef = useRef(null);
  const listId = useId();
  const current = services.find((s) => s.id === value) ?? services[0];

  const choose = (id) => {
    onChange(id);
    setOpen(false);
    buttonRef.current?.focus();
  };
  const onListKey = (e) => {
    const items = [...e.currentTarget.querySelectorAll('[role="option"]')];
    const index = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown" ? Math.min(index + 1, items.length - 1) : Math.max(index - 1, 0);
      items[next]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative w-full max-w-xs">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-full border bg-card px-4 text-sm font-medium text-foreground outline-none focus-visible:border-foreground/40",
          open ? "border-foreground/40" : "border-border",
        )}
      >
        <span className="truncate">{current.title}</span>
        <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          onKeyDown={onListKey}
          ref={(node) => node?.querySelector('[aria-selected="true"]')?.focus()}
          className="ent-pop-in absolute left-0 right-0 top-full z-30 mt-1 flex flex-col overflow-hidden rounded-md border border-border bg-card p-1 shadow-md"
        >
          {services.map((service) => (
            <li
              key={service.id}
              role="option"
              aria-selected={service.id === value}
              tabIndex={-1}
              onClick={() => choose(service.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  choose(service.id);
                }
              }}
              className="relative flex cursor-pointer items-center rounded-sm py-1.5 pl-2 pr-8 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
            >
              {service.title}
              {service.id === value && <Check className="absolute right-2 size-4" aria-hidden="true" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ServiceHeading({ service, className }) {
  return (
    <h3 className={className}>
      {service.heading
        ? service.heading.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))
        : service.title}
    </h3>
  );
}

function ServicePanel({ service, accent }) {
  const colors = ACCENTS[accent];
  const { ref, inView } = useInView({ threshold: 0.35 });
  return (
    <div
      ref={ref}
      className="relative w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.35)]"
    >
      <div className="flex items-center justify-between gap-4 border-b border-border pb-4">
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-foreground">{service.panelTitle}</p>
          <p className="text-xs text-muted-foreground">{service.panelSubtitle}</p>
        </div>
        {service.status ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-surface-subtle px-2.5 py-1 text-xs font-medium text-foreground">
            <span className={cn("size-1.5 rounded-full", colors.dot)} aria-hidden="true" />
            {service.status}
          </span>
        ) : null}
      </div>
      <p className="pt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{service.listLabel}</p>
      <ul className="mt-3 flex flex-col gap-2.5">
        {service.items?.map((item, index) => (
          <li
            key={item}
            style={{ transitionDelay: inView ? `${150 + index * 140}ms` : "0ms" }}
            className={cn(
              "flex items-center gap-3 text-sm text-foreground transition-all duration-500 ease-out motion-reduce:transition-none",
              inView ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
            )}
          >
            <span className={cn("flex size-5 shrink-0 items-center justify-center rounded-full", colors.check)}>
              <Check className="size-3" strokeWidth={3} aria-hidden="true" />
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RentShieldCard() {
  const [serviceId, setServiceId] = useState(RENTSHIELD_SERVICES[0].id);
  const service = RENTSHIELD_SERVICES.find((s) => s.id === serviceId) ?? RENTSHIELD_SERVICES[0];

  return (
    <article className="relative flex flex-col overflow-hidden rounded-xl border border-border bg-card lg:col-span-2 lg:row-span-2">
      <div className="flex flex-col gap-5 p-7 md:p-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <ProductLogo product="RentShield" logo={RENTSHIELD_INSHUR_LOGO} />
          <ServiceSelect
            label="Choose a RentShield service"
            services={RENTSHIELD_SERVICES}
            value={serviceId}
            onChange={setServiceId}
          />
        </div>
        <div key={service.id} className="ent-fade-in flex flex-col gap-3">
          <ServiceHeading
            service={service}
            className="max-w-lg font-heading text-3xl font-semibold leading-[1.1] tracking-tight text-foreground text-balance md:text-4xl"
          />
          <p className="max-w-lg text-lg leading-relaxed text-muted-foreground text-pretty md:whitespace-pre-line">{service.copy}</p>
        </div>
      </div>

      <div className="relative mt-auto flex flex-1 items-end px-7 pb-7 pt-4 md:px-10 md:pb-10">
        <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0", ACCENTS.coral.glow)} />
        <ServicePanel key={service.id} service={service} accent="coral" />
      </div>
    </article>
  );
}

function RentSuccessCard() {
  const service = RENTSUCCESS_SERVICE;
  return (
    <article className="relative flex flex-col overflow-hidden rounded-xl border border-border bg-card lg:row-span-2">
      <div className="flex flex-col gap-5 p-7 md:p-8">
        <ProductLogo product="RentSuccess" />
        <div className="flex flex-col gap-3">
          <h3 className="font-heading text-2xl font-semibold leading-tight tracking-tight text-foreground text-balance md:text-3xl">
            {service.title}
          </h3>
          <p className="text-base leading-relaxed text-muted-foreground text-pretty md:whitespace-pre-line">{service.copy}</p>
        </div>
      </div>

      <div className="relative mt-auto flex flex-1 items-end px-7 pb-7 pt-4 md:px-8 md:pb-8">
        <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0", ACCENTS.violet.glow)} />
        <ServicePanel service={service} accent="violet" />
      </div>
    </article>
  );
}

function RentIDCard() {
  const [serviceId, setServiceId] = useState(RENTID_SERVICES[0].id);
  const service = RENTID_SERVICES.find((s) => s.id === serviceId) ?? RENTID_SERVICES[0];

  return (
    <article className="relative flex flex-col overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex flex-col gap-5 p-7 md:p-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <ProductLogo product="RentID" />
          <ServiceSelect label="Choose a RentID API" services={RENTID_SERVICES} value={serviceId} onChange={setServiceId} />
        </div>
        <div key={service.id} className="ent-fade-in flex flex-col gap-3">
          <ServiceHeading
            service={service}
            className="font-heading text-3xl font-semibold leading-[1.1] tracking-tight text-foreground text-balance md:text-4xl"
          />
          <p className="text-lg leading-relaxed text-muted-foreground text-pretty md:whitespace-pre-line">{service.copy}</p>
        </div>
      </div>

      <div className="relative mt-auto flex flex-1 items-end px-7 pb-7 pt-4 md:px-10 md:pb-10">
        <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0", ACCENTS.blue.glow)} />
        <ServicePanel key={service.id} service={service} accent="blue" />
      </div>
    </article>
  );
}

function RentDirectCard() {
  const service = RENTDIRECT_SERVICE;
  return (
    <article className="relative flex flex-col overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex flex-col gap-5 p-7 md:p-10">
        <div className="flex h-10 items-center">
          <ProductLogo product="RentDirect" />
        </div>
        <div className="flex flex-col gap-3">
          <h3 className="font-heading text-3xl font-semibold leading-[1.1] tracking-tight text-foreground text-balance md:text-4xl">
            {service.title}
          </h3>
          <p className="text-lg leading-relaxed text-muted-foreground text-pretty md:whitespace-pre-line">{service.copy}</p>
        </div>
      </div>

      <div className="relative mt-auto flex flex-1 items-end px-7 pb-7 pt-4 md:px-10 md:pb-10">
        <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0", ACCENTS.mint.glow)} />
        <ServicePanel service={service} accent="mint" />
      </div>
    </article>
  );
}

export function EnterpriseEcosystem() {
  return (
    <section id="products" className="scroll-mt-20 border-t border-border bg-surface-subtle">
      <div className="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-24 sm:px-6 md:gap-16 md:py-32">
        <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-12">
          <h2 className="max-w-xl font-heading text-4xl font-semibold leading-[1.02] tracking-tighter text-foreground text-balance sm:text-5xl">
            Connect any service. Use one, or all of them.
          </h2>
          <p className="max-w-md text-lg leading-relaxed text-muted-foreground text-pretty">
            Plug individual capabilities into your existing app, or build from scratch with us.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <RentShieldCard />
          <RentSuccessCard />
          <div className="grid grid-cols-1 gap-4 lg:col-span-3 lg:grid-cols-2">
            <RentIDCard />
            <RentDirectCard />
          </div>
        </div>
      </div>
    </section>
  );
}
